import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Navigation } from '@/components/layout/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  User, 
  Mail, 
  Calendar,
  AlertTriangle,
  Save,
  Loader2,
  X
} from 'lucide-react';
import { format } from 'date-fns';

const COMMON_ALLERGENS = [
  'Milk',
  'Eggs',
  'Fish',
  'Shellfish',
  'Tree Nuts',
  'Peanuts',
  'Wheat',
  'Soy',
  'Sesame',
  'Gluten',
  'Corn',
  'Sulfites',
  'Mustard',
  'Celery',
  'Lupin'
];

const ProfilePage = () => {
  const { user, updateAllergens } = useAuth();
  const [selectedAllergens, setSelectedAllergens] = useState(user?.allergens || []);
  const [customAllergen, setCustomAllergen] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleAllergen = (allergen) => {
    setSelectedAllergens(prev => 
      prev.includes(allergen)
        ? prev.filter(a => a !== allergen)
        : [...prev, allergen]
    );
  };

  const addCustomAllergen = () => {
    const allergen = customAllergen.trim();
    if (allergen && !selectedAllergens.includes(allergen)) {
      setSelectedAllergens([...selectedAllergens, allergen]);
      setCustomAllergen('');
    }
  };

  const removeAllergen = (allergen) => {
    setSelectedAllergens(prev => prev.filter(a => a !== allergen));
  };

  const handleSaveAllergens = async () => {
    setSaving(true);
    try {
      await updateAllergens(selectedAllergens);
      toast.success('Allergen preferences saved!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(selectedAllergens.sort()) !== JSON.stringify((user?.allergens || []).sort());

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Navigation />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Profile & Settings
          </h1>
          <p className="text-muted-foreground text-lg">
            Manage your account and allergen preferences
          </p>
        </div>

        <div className="space-y-8">
          {/* Account Info */}
          <Card className="border border-border">
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>Your account details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg" data-testid="profile-name">{user?.name}</h3>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid gap-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{user?.email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">
                    Joined {user?.created_at ? format(new Date(user.created_at), 'MMMM d, yyyy') : 'Recently'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Allergen Preferences */}
          <Card className="border border-border">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <CardTitle>Allergen Alerts</CardTitle>
              </div>
              <CardDescription>
                Select your allergens to receive warnings when scanning products
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Selected allergens */}
              {selectedAllergens.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm">Your Allergens</Label>
                  <div className="flex flex-wrap gap-2" data-testid="selected-allergens">
                    {selectedAllergens.map((allergen) => (
                      <Badge 
                        key={allergen} 
                        variant="secondary" 
                        className="py-1.5 px-3 flex items-center gap-1"
                      >
                        {allergen}
                        <button
                          onClick={() => removeAllergen(allergen)}
                          className="ml-1 hover:text-destructive"
                          data-testid={`remove-allergen-${allergen}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Common allergens grid */}
              <div className="space-y-3">
                <Label className="text-sm">Common Allergens</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {COMMON_ALLERGENS.map((allergen) => (
                    <div key={allergen} className="flex items-center space-x-2">
                      <Checkbox
                        id={allergen}
                        checked={selectedAllergens.includes(allergen)}
                        onCheckedChange={() => toggleAllergen(allergen)}
                        data-testid={`allergen-checkbox-${allergen.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <label
                        htmlFor={allergen}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {allergen}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Custom allergen */}
              <div className="space-y-2">
                <Label htmlFor="custom-allergen" className="text-sm">Add Custom Allergen</Label>
                <div className="flex gap-2">
                  <Input
                    id="custom-allergen"
                    placeholder="e.g., Kiwi, Latex"
                    value={customAllergen}
                    onChange={(e) => setCustomAllergen(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomAllergen())}
                    data-testid="custom-allergen-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={addCustomAllergen}
                    disabled={!customAllergen.trim()}
                    data-testid="add-custom-allergen-btn"
                  >
                    Add
                  </Button>
                </div>
              </div>

              {/* Save button */}
              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveAllergens}
                  disabled={saving || !hasChanges}
                  data-testid="save-allergens-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Preferences
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Info */}
          <div className="p-6 bg-muted/50 rounded-xl">
            <h3 className="font-semibold mb-2">How Allergen Alerts Work</h3>
            <p className="text-sm text-muted-foreground">
              When you scan a product, our AI will check for any ingredients that match your allergen 
              preferences. You'll receive prominent warnings if any allergens are detected, helping 
              you make safer food choices.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
